import { ContextUrlInput } from "@/components/ContextUrlInput";
import { EnvironmentTypeSelect } from "@/components/EnvironmentTypeSelect";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Dialog, DialogContent } from "@/components/podkit/modal/Modal";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { ProjectSelectorLayout } from "@/components/projects/ProjectSelectorModal";
import { useContextURL, useListContextUrls, useSetContextUrls } from "@/hooks/use-context-url";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { type EnvironmentTypeEntry, useEnvironmentTypes } from "@/hooks/use-environment-types";
import { useSCMAuthentication } from "@/hooks/use-scm-authentication.ts";
import { type TrackLocation, TrackLocations } from "@/hooks/use-segment";
import { type PlainRunnerEnvironmentClass, useCreateEnvironment } from "@/queries/environment-queries";
import { isInvalidArgumentError, isNotFoundError, isUnauthorizedError } from "@/queries/errors";
import { useListProjects } from "@/queries/project-queries";
import {
    useIsUserAuthenticatedWithRunner,
    useParseContextURL,
    type PlainAuthenticationMethodPersonalAccessToken,
} from "@/queries/runner-queries";
import { SCMAuthentication, type SCMAuthenticationModalProps } from "@/routes/environments/create/SCMAuthentication";
import { getDetailsURL } from "@/routes/environments/details-url";
import { formatError } from "@/utils/errors";
import { ConnectError } from "@connectrpc/connect";
import { getScmProviderName } from "frontend-shared/local-runner";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { ParseContextURLPreconditionFailureDetails, RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { Cable } from "lucide-react";
import { type FC, type FormEvent, useCallback, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

export type CreateEnvironmentStage =
    | { type: "input" }
    | {
          type: "scm-authentication";
          authenticationUrl: string;
          patAuth?: PlainAuthenticationMethodPersonalAccessToken;
          scmId: string;
          clazz: PlainRunnerEnvironmentClass;
      };

export const CreateEnvironmentDetailsLayout: FC<{
    onClose: () => void;
    onCreateSuccess?: () => void;
    initialContextURL: string;
}> = ({ onClose, onCreateSuccess, initialContextURL }) => {
    const navigate = useNavigate();
    useDocumentTitle("Create Environment");

    const { contextUrls } = useListContextUrls();
    const setContextUrls = useSetContextUrls();
    const { toast } = useToast();

    const [stage, setStage] = useState<CreateEnvironmentStage>({ type: "input" });

    const [selectedEnvironment, setSelectedEnvironment] = useState<EnvironmentTypeEntry>();

    const { contextURL, setContextURL, contextURLValid } = useContextURL(initialContextURL);
    const [debouncedContext] = useDebounce({ contextURL, contextURLValid }, 1000, {
        trailing: true,
        equalityFn: (left, right) => {
            return left.contextURL == right.contextURL && left.contextURLValid == right.contextURLValid;
        },
    });

    const isTypingContextURL = contextURL !== debouncedContext.contextURL;

    const { envTypes, isLoading: isLoadingEnvTypes } = useEnvironmentTypes();

    const hasNoEnvTypes = !isLoadingEnvTypes && envTypes.length == 0;

    const {
        isLoading: isLoadingParseContext,
        isSuccess: isSuccessParseContext,
        error: errorParseContext,
    } = useParseContextURL(selectedEnvironment?.runner?.runnerId, debouncedContext.contextURL, {
        enabled: debouncedContext.contextURLValid,
    });

    const parseContextFailed = !isLoadingParseContext && isUnauthorizedError(errorParseContext);
    const isNotFound = isNotFoundError(errorParseContext);
    const isInvalidArgument = isInvalidArgumentError(errorParseContext);

    const {
        data: authCheckResponse,
        isLoading: isLoadingAuthCheck,
        isSuccess: isSuccessAuthCheck,
        isError: isErrorAuthCheck,
    } = useIsUserAuthenticatedWithRunner(selectedEnvironment?.runner?.runnerId, debouncedContext.contextURL, {
        refetchUntilAuthenticated: true,

        // Auth check should start be polled for once the parseContextURL failed with an unauthorized error
        enabled: debouncedContext.contextURLValid && parseContextFailed,
    });
    const isAuthenticationRequired = parseContextFailed || authCheckResponse?.type == "AuthenticationRequired";

    const getScmName = () => {
        let scmID = "";
        if (isAuthenticationRequired) {
            if (parseContextFailed) {
                if (errorParseContext instanceof ConnectError) {
                    const details = errorParseContext.findDetails(ParseContextURLPreconditionFailureDetails);
                    for (const detail of details) {
                        if (detail.scmId) {
                            scmID = detail.scmId;
                        }
                    }
                }
            } else if (authCheckResponse?.type == "AuthenticationRequired") {
                scmID = authCheckResponse.scmId;
            }
        }

        return getScmProviderName(scmID);
    };

    const createEnvironment = useCreateEnvironment();

    // Select first environment type by default
    if (!isLoadingEnvTypes && !selectedEnvironment && envTypes.length > 0) {
        const firstActive = envTypes.find((et) => et.runner?.status?.phase === RunnerPhase.ACTIVE);
        if (firstActive) {
            setSelectedEnvironment((prev) => {
                if (prev) {
                    return prev;
                }
                return firstActive;
            });
        }
    }

    // If the selected environment isn't present in the list of environment classes then reset the selection.
    // This can happen if you change the context URL after having selected an environment type
    if (!isLoadingEnvTypes && selectedEnvironment) {
        const selectedIsInList = envTypes?.find(
            (v) =>
                v.clazz?.id == selectedEnvironment.clazz?.id &&
                v.runner?.runnerId == selectedEnvironment?.runner?.runnerId,
        );
        if (!selectedIsInList) {
            setSelectedEnvironment(undefined);
        }
    }

    const handleOnContinue = useCallback(async () => {
        let environment;
        try {
            environment = await createEnvironment.mutateAsync({
                type: "contextUrl",
                contextURL: contextURL || "",
                classID: selectedEnvironment?.clazz?.id || "",
            });
            if (contextURL) {
                await setContextUrls([contextURL, ...(contextUrls || []).filter((s) => s !== contextURL)]);
            }
            onCreateSuccess?.();
            navigate(getDetailsURL(environment));
        } catch (error) {
            toast({ title: "Failed to create an environment", description: formatError(error) });
        }
    }, [
        contextURL,
        selectedEnvironment,
        createEnvironment,
        contextUrls,
        navigate,
        toast,
        onCreateSuccess,
        setContextUrls,
    ]);

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            const formData = new FormData(event.currentTarget);
            const runnerID = selectedEnvironment?.runner?.runnerId;
            const classID = selectedEnvironment?.clazz?.id;
            const contextURL = formData.get("contextURL") as string;

            if (!runnerID || !contextURL || !classID) {
                console.log(`Invalid runnerId=${runnerID} or contextURL=${contextURL} or classID=${classID}`);
                return;
            }

            if (authCheckResponse?.type == "AuthenticationRequired") {
                setStage({
                    type: "scm-authentication",
                    authenticationUrl: authCheckResponse.url,
                    patAuth: authCheckResponse.patAuth,
                    scmId: authCheckResponse.scmId,
                    clazz: selectedEnvironment?.clazz,
                });
                return;
            }

            await handleOnContinue();
        },
        [handleOnContinue, selectedEnvironment, authCheckResponse],
    );

    const scmSettingsLink = useMemo(() => {
        if (selectedEnvironment === undefined) {
            return undefined;
        }

        // Remote runners have their own settings page. Local runners are configured on an org level in the runner settings.
        return selectedEnvironment.runner.kind === RunnerKind.REMOTE
            ? `/settings/runners/${selectedEnvironment.runner.runnerId}`
            : "/settings/runners";
    }, [selectedEnvironment]);

    const createButtonDisabled =
        selectedEnvironment == undefined ||
        !contextURLValid ||
        isNotFound ||
        !(isSuccessParseContext || isSuccessAuthCheck);

    const createButtonText = isAuthenticationRequired ? "Authenticate and Create" : "Create";

    if (stage.type === "scm-authentication") {
        return (
            <div className="flex w-full flex-col items-center justify-center gap-4">
                <Authentication
                    repoURL={contextURL || ""}
                    authenticationUrl={stage.authenticationUrl}
                    patAuth={stage.patAuth}
                    scmId={stage.scmId}
                    onClose={onClose}
                    onClickContinue={handleOnContinue}
                    clazz={stage.clazz}
                    data-track-location={TrackLocations.CreateEnvironmentSCMAuthenticationModal}
                />
            </div>
        );
    }

    const showIsVerifying =
        (!isAuthenticationRequired && contextURLValid && isLoadingParseContext && !hasNoEnvTypes) ||
        (isLoadingAuthCheck && isErrorAuthCheck);

    if (isLoadingEnvTypes) {
        return <div className="h-[370px]" data-testid="loading-env-types" />;
    }

    if (hasNoEnvTypes) {
        return (
            <div
                className="flex flex-col items-center gap-4 overflow-hidden px-5 py-4"
                data-testid="no-runner-configured-content"
            >
                <div className="flex w-full flex-col items-center gap-4">
                    <div className="flex max-w-[330px] flex-col items-center gap-4">
                        <Heading1 className="text-wrap text-center text-2xl font-bold text-content-primary">
                            You need to set up a runner before you can create environments
                        </Heading1>
                        <Cable className="size-[60px] text-content-secondary" strokeWidth={1} />
                        <Text className="text-wrap text-center text-xl text-content-secondary">
                            A runner is a service that runs your development environment. They can be set up on your
                            local machine or in the cloud.
                        </Text>
                    </div>
                    <Button variant="primary" className="w-full" asChild>
                        <Link to="/onboarding/set-up-runner" onClick={onClose} data-testid="runner-onboarding-link">
                            Set Up a Runner
                        </Link>
                    </Button>
                </div>
            </div>
        );
    }

    const showAccessSettingsButton = !isTypingContextURL && isInvalidArgument && !!scmSettingsLink;

    return (
        <div className="flex flex-col gap-4 overflow-hidden px-5 py-4" data-testid="create-environment-form">
            <div className="flex flex-col items-center justify-start">
                <Heading1>New environment</Heading1>
                <Text className="pt-1 text-base text-content-secondary">From a repository URL</Text>
            </div>
            <form onSubmit={handleSubmit} className="pt-8">
                <div className="flex flex-col gap-2" id="create-environment-form">
                    <ContextUrlInput
                        label="Repository URL"
                        name="contextURL"
                        value={contextURL}
                        onChange={setContextURL}
                        errorMessage={!!contextURL && !contextURLValid ? "The URL must point to a Git repository." : ""}
                        className={"pb-2"}
                        autoFocus
                    />

                    <EnvironmentTypeSelect
                        label="Environment class"
                        value={selectedEnvironment}
                        onChange={(v) => setSelectedEnvironment(v)}
                        name="environment-type"
                        loading={isLoadingEnvTypes}
                        disabled={!contextURLValid}
                        envTypes={envTypes}
                    />

                    <div
                        data-testid="errors"
                        className="flex min-h-14 flex-col items-center justify-center gap-2 text-sm"
                    >
                        {showIsVerifying && <span data-testid="verifying">Verifying repository and connection…</span>}
                        {isAuthenticationRequired && (
                            <span className="max-w-80 text-wrap">
                                <span className="font-bold">
                                    {selectedEnvironment?.runner?.name || "Unknown runner"}
                                </span>{" "}
                                requires authentication with <span className="font-bold">{getScmName()}</span>
                            </span>
                        )}
                        {isNotFound && (
                            <span>The repository could not be found. Please check the URL and try again.</span>
                        )}
                        {hasNoEnvTypes && <NoEnvTypesError onClose={onClose} />}
                        {!showIsVerifying &&
                            !isAuthenticationRequired &&
                            !isNotFound &&
                            !hasNoEnvTypes &&
                            !isTypingContextURL &&
                            !!errorParseContext && <ParseContextError error={errorParseContext} />}
                    </div>

                    <div className="flex flex-col gap-2">
                        {showAccessSettingsButton && (
                            <Button variant="secondary" className="w-full" asChild>
                                <Link
                                    to={scmSettingsLink}
                                    onClick={onClose}
                                    data-testid="open-repository-settings-link"
                                >
                                    Open repository access settings
                                </Link>
                            </Button>
                        )}

                        <Button
                            type="submit"
                            variant="primary"
                            className={cn("w-full text-base font-bold", showAccessSettingsButton && "hidden")}
                            size={"md"}
                            loading={createEnvironment.isPending}
                            disabled={createButtonDisabled}
                            data-testid="create-environment-button"
                        >
                            {createButtonText}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export const ParseContextError: FC<{ error: Error }> = ({ error }) => {
    const errorMessage = useMemo(() => {
        if (error instanceof ConnectError) {
            return error.rawMessage;
        }
        return formatError(error);
    }, [error]);
    return <Text className="text-sm">{errorMessage}</Text>;
};

export const NoEnvTypesError: FC<{ onClose?: () => void }> = ({ onClose }) => {
    return (
        <Text className="text-base">
            To create an environment, please first{" "}
            <Link
                to="/onboarding/set-up-runner"
                onClick={onClose}
                className="text-content-link"
                data-testid="runner-onboarding-link"
            >
                set up a runner
            </Link>
            .
        </Text>
    );
};

export const CreateEnvironmentModal: FC<{
    onClose: () => void;
    "data-track-location": TrackLocation;
}> = ({ onClose, "data-track-location": dataTrackLocation }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { data: projectsData, isLoading, isPending } = useListProjects();

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
            navigate({ hash: "" }, { replace: true });
        }
    };

    const [showCreateFromUrl, setShowCreateFromUrl] = useState(!!location.hash);
    const [initialCreateFromURL, setInitialCreateFromURL] = useState<string>("");

    if (isLoading || isPending) {
        return <LoadingState />;
    }

    const doShowCreateFromUrl = showCreateFromUrl || projectsData?.projects.length === 0;

    return (
        <Dialog open onOpenChange={onOpenChange}>
            <DialogContent
                data-track-location={dataTrackLocation}
                className={cn(
                    !doShowCreateFromUrl && "min-h-[560px] w-[800px] max-w-[800px]",
                    doShowCreateFromUrl && "max-w-128 min-h-[220px] w-128",
                )}
            >
                {!doShowCreateFromUrl && (
                    <ProjectSelectorLayout
                        onClose={onClose}
                        onCreateFromUrl={(url?: string) => {
                            setInitialCreateFromURL(url || "");
                            setShowCreateFromUrl(true);
                        }}
                    />
                )}
                {doShowCreateFromUrl && (
                    <CreateEnvironmentDetailsLayout
                        onClose={onClose}
                        onCreateSuccess={onClose}
                        initialContextURL={initialCreateFromURL}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

const Authentication: FC<SCMAuthenticationModalProps> = (p) => {
    const props = useSCMAuthentication({
        repoURL: p.repoURL,
        authenticationUrl: p.authenticationUrl,
        clazz: p.clazz,
    });
    return <SCMAuthentication {...{ ...p, ...props }} />;
};
